import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Modal from 'cccisd-modal';
import Tooltip from 'cccisd-tooltip';
import notification from 'cccisd-notification';
import axios from 'cccisd-axios';
import { getFormattedDate } from 'js/helpers';
import TakeAssessment from '../TakeAssessment';
import ContactForm from '../ManageReporters/ContactForm';
import InviteModal from './InviteModal';
import style from '../style.css';
import { Formik, Form, Field, CccisdSelect } from 'cccisd-formik';
import { gradeOptions } from 'js/assessment/components/DeploymentCenter/EditStudentForm';

// Icons
import IconQuill from 'cccisd-icons/quill3';
import IconCopy from 'cccisd-icons/copy3';
import IconCheckmark from 'cccisd-icons/checkmark-circle';
import { IconInProgress } from 'images/icons.js';
import IconStop from 'cccisd-icons/cancel';
import IconMail from 'cccisd-icons/paperplane';

const Boilerplate = window.cccisd.boilerplate;

const AssessmentActions = ({ row, setForceReloadQueries, progress, surveyProgress, setSurveyProgress }) => {
    const [lastVisitedDate, setLastVisitedDate] = useState(null);
    const [showContactForm, setShowContactForm] = useState(!row.name);
    const [showGradeForm, setShowGradeForm] = useState(!row.assessment.selectedByPawn?.fields?.grade);
    const studentName = `${row.assessment.selectedByPawn.fields.firstName} ${row.assessment.selectedByPawn.fields.lastName}`;
    const studentPawn = row.assessment.selectedByPawn?.pawn;
    const reporterTypeLower = useMemo(() => row.reporterType?.toLowerCase(), [row.reporterType]);
    const assessmentLink = Boilerplate.url(
        `/sessionPlayer/${row.deploymentId}/${row.assessment.assignmentPlanId}/${row.session.sessionId}/${
            row.session.settings.reporter
        }/${row.pawnId}/${row.pawnHash}?s=${row.session.settings.options.join(',')}`
    );

    useEffect(() => {
        if (progress && progress.length > 0) {
            const sessionProgress = progress.find(
                p => p.pawnId === row.pawnId && p.sessionId === row.session.sessionId
            );
            if (sessionProgress) {
                setLastVisitedDate(sessionProgress.lastVisitedDate);
                const surveyProgressStatus = sessionProgress.surveyProgressStatus;
                if (surveyProgressStatus) {
                    setSurveyProgress(prev => ({
                        ...prev,
                        [`${row.session.sessionId}_${row.pawnId}`]: surveyProgressStatus,
                    }));
                }
            }
        }
    }, [progress, row.pawnId, row.options]);

    useEffect(() => {
        setShowContactForm(!row.name);
    }, [row.name]);

    useEffect(() => {
        setShowGradeForm(!row.assessment.selectedByPawn?.fields?.grade);
    }, [row.assessment.selectedByPawn?.fields?.grade]);

    const renderStoppedAssessmentList = () => {
        return (
            <div>
                {row.options.map(surveyHandle => {
                    if (
                        surveyProgress &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`] &&
                        surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle] === 2
                    ) {
                        return (
                            <div key={surveyHandle}>
                                <span className="text-success">
                                    <IconCheckmark spaceRight />
                                </span>
                                Completed{' '}
                                {getFormattedDate(
                                    surveyProgress[`${row.session.sessionId}_${row.pawnId}`][
                                        surveyHandle + '_lastVisit'
                                    ]
                                )}
                            </div>
                        );
                    }
                    return (
                        <div key={surveyHandle}>
                            <span className="text-danger">
                                <IconStop spaceRight />
                            </span>
                            Stopped {getFormattedDate(row.stopped)}
                        </div>
                    );
                })}
            </div>
        );
    };

    const surveyCompletedCount = useMemo(() => {
        let completedCount = 0;
        for (const surveyHandle of row.options) {
            if (surveyProgress && surveyProgress[`${row.session.sessionId}_${row.pawnId}`]) {
                const surveyStatus = surveyProgress[`${row.session.sessionId}_${row.pawnId}`][surveyHandle];
                if (surveyStatus === 2) {
                    completedCount++;
                }
            }
        }
        return completedCount;
    }, [row.options, surveyProgress, row.session.sessionId, row.pawnId]);

    const renderProgressStatus = () => {
        return (
            <>
                {surveyCompletedCount > 0 ? (
                    <span>
                        {surveyCompletedCount} of {row.options.length} complete | Last visited:{' '}
                        {getFormattedDate(lastVisitedDate)}
                    </span>
                ) : (
                    <span>In Progress | Last visited: {getFormattedDate(lastVisitedDate)}</span>
                )}
            </>
        );
    };

    const renderContactInfoForm = (callback = () => {}) => {
        return (
            <>
                <div className="alert alert-primary" role="alert">
                    {`No reporter information has been provided. Please take a moment to update the reporter's information.`}
                </div>
                <ContactForm
                    action="edit"
                    contactType={row.session.settings.reporter}
                    closeModal={() => {
                        setShowContactForm(false);
                        if (!showGradeForm) {
                            callback();
                        }
                    }}
                    session={row.session}
                    plan={row.assessment}
                    refreshContactsData={() => {}}
                    refreshPlanData={() => {}}
                    pawn={{ pawnId: row.pawnId, pawnHash: row.pawnHash }}
                    initialValues={{ name: row.name, email: row.email }}
                />
            </>
        );
    };

    const renderStudentGradeForm = (callback = () => {}) => {
        return (
            <>
                <div className="alert alert-primary" role="alert">
                    {`Student grade is required. Please select the student's grade to continue.`}
                </div>
                <Formik
                    initialValues={{
                        grade: row.assessment.selectedByPawn?.fields?.grade || '',
                    }}
                    validate={values => {
                        const errors = {};
                        if (!values.grade) {
                            errors.grade = 'Grade is required';
                        }
                        return errors;
                    }}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={async (values, { setSubmitting }) => {
                        if (!studentPawn?.pawnId || !studentPawn?.pawnHash) {
                            setSubmitting(false);
                            return;
                        }

                        try {
                            const response = await axios.put(
                                Boilerplate.route('api.nexus.pawn.update', {
                                    pawn: studentPawn.pawnId,
                                }),
                                {
                                    pawnHash: studentPawn.pawnHash,
                                    grade: values.grade,
                                }
                            );

                            if (response?.data?.status === 'success') {
                                setShowGradeForm(false);
                                setForceReloadQueries();
                                callback();
                            } else {
                                // console.error('Failed to update student grade', response);
                            }
                        } catch (error) {
                            console.error(error);
                        }

                        setSubmitting(false);
                    }}
                >
                    {({ errors, isSubmitting }) => (
                        <Form>
                            <label>
                                <b>Grade:</b>
                            </label>
                            <Field name="grade" component={CccisdSelect} options={gradeOptions} />
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    marginTop: '1em',
                                }}
                            >
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                                    Submit
                                </button>
                            </div>
                        </Form>
                    )}
                </Formik>
            </>
        );
    };

    const renderMissingInfoForm = (callback = () => {}) => {
        return (
            <>
                {showContactForm && <>{renderContactInfoForm(callback)}</>}
                {!showContactForm && showGradeForm && renderStudentGradeForm(callback)}
            </>
        );
    };

    const handleCopyLink = () => {
        return navigator.clipboard.writeText(assessmentLink).then(
            () => {
                notification({
                    message: 'Copied URL to clipboard',
                    type: 'success',
                });
            },
            err => {
                notification({
                    message: 'Failed to copy URL to clipboard',
                    type: 'error',
                });
            }
        );
    };

    if (row.session.completedDate || surveyCompletedCount === row.options.length) {
        const lastSurveyHandle = row.options[row.options.length - 1];
        /* eslint-disable-next-line */
        const lastSurveyCompletedDate = surveyProgress
            ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`]
                ? surveyProgress[`${row.session.sessionId}_${row.pawnId}`][lastSurveyHandle + '_lastVisit']
                : null
            : null;
        return (
            <>
                <span className="text-success">
                    <IconCheckmark spaceRight />
                </span>
                Completed {row.session.completedDate || getFormattedDate(lastSurveyCompletedDate)}
            </>
        );
    }

    return (
        <div className={style.assessmentActionsWrapper}>
            {row.stopped ? (
                <>{renderStoppedAssessmentList()}</>
            ) : (
                <div>
                    <div className={style.assessmentActions}>
                        <Modal
                            title={
                                showContactForm || showGradeForm
                                    ? `Update information for ${studentName}`
                                    : 'Take Assessment'
                            }
                            trigger={
                                <Tooltip
                                    title={`${
                                        row.name
                                            ? `Begin assessment(s) for ${row.name}.`
                                            : `Begin assessment(s) for this ${reporterTypeLower || 'reporter'}.`
                                    }`}
                                >
                                    <button className="btn btn-primary btn-sm" type="button">
                                        <IconQuill /> Take Now
                                    </button>
                                </Tooltip>
                            }
                            beforeClose={() => {
                                setForceReloadQueries();
                            }}
                            size="large"
                            render={({ closeModal }) => {
                                if (showContactForm || showGradeForm) {
                                    return renderMissingInfoForm();
                                }
                                return (
                                    <TakeAssessment
                                        session={row.session}
                                        reporterName={row.name}
                                        assessmentLink={assessmentLink}
                                        closeModal={closeModal}
                                        studentName={studentName}
                                    />
                                );
                            }}
                        />
                        {showContactForm || showGradeForm ? (
                            <Modal
                                title={`Update information for ${studentName}`}
                                trigger={
                                    <>
                                        {row.numberOfTimesInvited > 0 ? (
                                            <Tooltip
                                                title={`${
                                                    row.numberOfTimesInvited
                                                } invitations previously sent, most recently on ${getFormattedDate(
                                                    row.messages?.[0]?.sentAt
                                                )}`}
                                            >
                                                <button className="btn btn-warning btn-sm" type="button">
                                                    <IconMail /> Resend
                                                </button>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip
                                                title={`Invite ${
                                                    row.name ? row.name : `this ${reporterTypeLower}`
                                                } to complete assessment(s).`}
                                            >
                                                <button className="btn btn-secondary btn-sm" type="button">
                                                    <IconMail /> Invite
                                                </button>
                                            </Tooltip>
                                        )}
                                    </>
                                }
                                size="large"
                                render={({ closeModal }) => {
                                    return renderMissingInfoForm(() => {
                                        closeModal();
                                    });
                                }}
                            />
                        ) : (
                            <InviteModal
                                row={row}
                                assessmentLink={assessmentLink}
                                setForceReloadQueries={setForceReloadQueries}
                            />
                        )}
                        <Tooltip
                            title={`Copy the link to send the assessment(s) to ${
                                row.name ?? `this ${reporterTypeLower || 'reporter'}`
                            }.`}
                        >
                            {showContactForm || showGradeForm ? (
                                <Modal
                                    title={`Update information for ${studentName}`}
                                    trigger={
                                        <button className="btn btn-secondary btn-sm" type="button">
                                            <IconCopy /> Copy Link
                                        </button>
                                    }
                                    size="large"
                                    render={({ closeModal }) =>
                                        renderMissingInfoForm(() =>
                                            handleCopyLink().then(() => {
                                                setForceReloadQueries();
                                                closeModal();
                                            })
                                        )
                                    }
                                />
                            ) : (
                                <button className="btn btn-secondary btn-sm" type="button" onClick={handleCopyLink}>
                                    <IconCopy /> Copy Link
                                </button>
                            )}
                        </Tooltip>
                    </div>
                    <div className={style.assessmentActionsProgress}>
                        {lastVisitedDate && (
                            <span style={{ fontSize: 'smaller', fontWeight: '300' }}>
                                <span style={{ color: '#154988', marginRight: '0.3em' }}>
                                    <IconInProgress />
                                </span>
                                {renderProgressStatus()}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default AssessmentActions;

AssessmentActions.propTypes = {
    row: PropTypes.object.isRequired,
    setForceReloadQueries: PropTypes.func.isRequired,
    progress: PropTypes.array.isRequired,
    surveyProgress: PropTypes.object,
    setSurveyProgress: PropTypes.func.isRequired,
};
AssessmentActions.defaultProps = {};
